import { useMemo, useState } from 'react';
import {
    Box, Button, Checkbox, Chip, CircularProgress, FormControlLabel,
    Grid, MenuItem, Paper, TextField, Typography,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid';
import {
    ArrowDropUp as ArrowUpIcon,
    ArrowDropDown as ArrowDownIcon,
} from '@mui/icons-material';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid,
    Tooltip, ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';
import DateRangeField from '../../components/ui/DateRangeField';
import ProductFilters, { buildFilterParams } from '../../components/ui/ProductFilters';

const CHART_COLORS = [
    '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe',
    '#00C49F', '#FFBB28', '#FF8042', '#a4de6c', '#d0ed57',
    '#8dd1e1', '#83a6ed', '#e6194b', '#3cb44b', '#ffe119',
];

const METRIC_ROWS = [
    { key: 'open_card', label: 'Переходы', group: 'traffic', fmt: 'int' },
    { key: 'add_to_cart', label: 'В корзину', group: 'traffic', fmt: 'int' },
    { key: 'orders', label: 'Заказы', group: 'traffic', fmt: 'int' },
    { key: 'avg_position', label: 'Позиция', group: 'position', fmt: 'dec1', lowerBetter: true },
    { key: 'visibility', label: 'Видимость', group: 'position', fmt: 'pct1' },
    { key: 'open_to_cart', label: 'CR в корзину', group: 'conversion', fmt: 'pct2' },
    { key: 'cart_to_order', label: 'CR в заказ', group: 'conversion', fmt: 'pct2' },
];

const METRIC_GROUP_COLORS = {
    traffic: '#e8f5e9',
    position: '#e3f2fd',
    conversion: '#fff3e0',
};

const METRICS = [
    { value: 'open_card', label: 'Переходы' },
    { value: 'add_to_cart', label: 'Корзина' },
    { value: 'orders', label: 'Заказы' },
    { value: 'avg_position', label: 'Позиция' },
    { value: 'visibility', label: 'Видимость' },
    { value: 'open_to_cart', label: 'CR корзина %' },
    { value: 'cart_to_order', label: 'CR заказ %' },
];

const BREAKDOWNS = [
    { value: 'gruppa', label: 'Группа' },
    { value: 'podgruppa', label: 'Подгруппа' },
    { value: 'article', label: 'Товар' },
];

const fmtYmd = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const fmtNum = (v, dec = 0) => {
    if (v == null || v === '') return '-';
    const n = Number(v);
    if (isNaN(n)) return '-';
    return n.toLocaleString('ru-RU', { minimumFractionDigits: dec, maximumFractionDigits: dec });
};

const cellStyle = { py: 0.5, px: 1, fontSize: '0.8rem', whiteSpace: 'nowrap' };
const headerStyle = { ...cellStyle, fontWeight: 'bold', bgcolor: 'grey.100' };

export default function AnalyticsSearch() {
    const { error: showError } = useNotify();

    const today = new Date();
    const minus7 = new Date(today);
    minus7.setDate(today.getDate() - 7);

    const [breakdown, setBreakdown] = useState('podgruppa');
    const [dateFrom, setDateFrom] = useState(fmtYmd(minus7));
    const [dateTo, setDateTo] = useState(fmtYmd(today));
    const [filters, setFilters] = useState({ mp: '1' });

    const [loading, setLoading] = useState(false);
    const [showDelta, setShowDelta] = useState(true);
    const [chartMetrics, setChartMetrics] = useState([]);
    const [selectedEntity, setSelectedEntity] = useState('__TOTAL__');

    const [summary, setSummary] = useState({ current: {}, prev: {} });
    const [pivotRows, setPivotRows] = useState([]);
    const [keywords, setKeywords] = useState([]);
    const [moversUp, setMoversUp] = useState({ position: [], traffic: [], conversion: [] });
    const [moversDown, setMoversDown] = useState({ position: [], traffic: [], conversion: [] });

    const toggleChartMetric = (key) => {
        setChartMetrics(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    };

    const buildParams = () => {
        const filterParams = buildFilterParams(filters);
        const p = { startDate: dateFrom, endDate: dateTo, ...filterParams };
        if (p.article) { p.offer_id = p.article; delete p.article; }
        delete p.mp;
        return p;
    };

    const load = async () => {
        setLoading(true);
        try {
            const p = buildParams();
            const [sumRes, pivRes, kwRes, muRes, mdRes] = await Promise.all([
                axios.get('/analytics/wb/search/summary', { params: p }),
                axios.get('/analytics/wb/search/pivot', { params: { ...p, breakdown } }),
                axios.get('/analytics/wb/search/keywords', { params: p }),
                axios.get('/analytics/wb/search/movers', { params: { ...p, direction: 'up' } }),
                axios.get('/analytics/wb/search/movers', { params: { ...p, direction: 'down' } }),
            ]);
            setSummary({ current: sumRes.data.current || {}, prev: sumRes.data.prev || {} });
            setPivotRows(pivRes.data.rows || []);
            setKeywords((kwRes.data.rows || []).map((r, i) => ({ id: i, ...r })));
            setMoversUp(muRes.data || { position: [], traffic: [], conversion: [] });
            setMoversDown(mdRes.data || { position: [], traffic: [], conversion: [] });
            setSelectedEntity('__TOTAL__');
            setChartMetrics([]);
        } catch (e) {
            showError(e?.response?.data?.message || e?.message || 'Ошибка загрузки');
        } finally {
            setLoading(false);
        }
    };

    // --- Pivot computation ---
    const { entities, dates, pivotMap, dateTotals, totals } = useMemo(() => {
        const dateSet = new Set();
        const entityMap = new Map();
        for (const r of pivotRows) {
            const entity = r.entity || 'Не указано';
            const dt = typeof r.dt === 'string' ? r.dt.split('T')[0] : r.dt;
            dateSet.add(dt);
            if (!entityMap.has(entity)) entityMap.set(entity, new Map());
            entityMap.get(entity).set(dt, {
                open_card: Number(r.open_card || 0), add_to_cart: Number(r.add_to_cart || 0),
                orders: Number(r.orders || 0), avg_position: Number(r.avg_position || 0),
                visibility: Number(r.visibility || 0), open_to_cart: Number(r.open_to_cart || 0),
                cart_to_order: Number(r.cart_to_order || 0),
            });
        }
        const dates = [...dateSet].sort();
        const entityArr = [...entityMap.keys()].sort((a, b) => {
            const sumA = [...entityMap.get(a).values()].reduce((s, c) => s + c.orders, 0);
            const sumB = [...entityMap.get(b).values()].reduce((s, c) => s + c.orders, 0);
            return sumB - sumA;
        });

        const dateTotals = {};
        for (const dt of dates) {
            let oc = 0, atc = 0, ord = 0, posSum = 0, visSum = 0, cnt = 0;
            for (const dMap of entityMap.values()) {
                const cell = dMap.get(dt);
                if (cell) { oc += cell.open_card; atc += cell.add_to_cart; ord += cell.orders; posSum += cell.avg_position; visSum += cell.visibility; cnt++; }
            }
            dateTotals[dt] = {
                open_card: oc, add_to_cart: atc, orders: ord,
                avg_position: cnt ? +(posSum / cnt).toFixed(1) : 0, visibility: cnt ? +(visSum / cnt).toFixed(1) : 0,
                open_to_cart: oc > 0 ? +(atc / oc * 100).toFixed(2) : 0, cart_to_order: atc > 0 ? +(ord / atc * 100).toFixed(2) : 0,
            };
        }

        const agg = (dMap) => {
            let oc = 0, atc = 0, ord = 0, posSum = 0, visSum = 0, cnt = 0;
            for (const cell of dMap.values()) {
                oc += cell.open_card; atc += cell.add_to_cart; ord += cell.orders;
                posSum += cell.avg_position; visSum += cell.visibility; cnt++;
            }
            return {
                open_card: oc, add_to_cart: atc, orders: ord,
                avg_position: cnt ? +(posSum / cnt).toFixed(1) : 0, visibility: cnt ? +(visSum / cnt).toFixed(1) : 0,
                open_to_cart: oc > 0 ? +(atc / oc * 100).toFixed(2) : 0, cart_to_order: atc > 0 ? +(ord / atc * 100).toFixed(2) : 0,
            };
        };

        const allDayMap = new Map();
        for (const dt of dates) allDayMap.set(dt, dateTotals[dt]);
        const totals = agg(allDayMap);

        return { entities: entityArr, dates, pivotMap: entityMap, dateTotals, totals };
    }, [pivotRows]);

    const activeData = useMemo(() => {
        if (selectedEntity === '__TOTAL__') return { dayMap: dateTotals, totals };
        const dMap = pivotMap.get(selectedEntity);
        if (!dMap) return { dayMap: {}, totals: {} };
        let oc = 0, atc = 0, ord = 0, posSum = 0, visSum = 0, cnt = 0;
        for (const cell of dMap.values()) {
            oc += cell.open_card; atc += cell.add_to_cart; ord += cell.orders;
            posSum += cell.avg_position; visSum += cell.visibility; cnt++;
        }
        const t = {
            open_card: oc, add_to_cart: atc, orders: ord,
            avg_position: cnt ? +(posSum / cnt).toFixed(1) : 0, visibility: cnt ? +(visSum / cnt).toFixed(1) : 0,
            open_to_cart: oc > 0 ? +(atc / oc * 100).toFixed(2) : 0, cart_to_order: atc > 0 ? +(ord / atc * 100).toFixed(2) : 0,
        };
        const obj = {};
        for (const [dt, cell] of dMap.entries()) obj[dt] = cell;
        return { dayMap: obj, totals: t };
    }, [selectedEntity, dateTotals, pivotMap, totals]);

    // --- Chart data (normalized like AnalyticsDaily) ---
    const { chartData, chartRaw, chartDomain } = useMemo(() => {
        if (!dates.length || !chartMetrics.length) return { chartData: [], chartRaw: [], chartDomain: [80, 120] };
        const means = {};
        for (const k of chartMetrics) means[k] = 0;
        const raw = dates.map(dt => {
            const cell = activeData.dayMap[dt] || {};
            const point = { dt: dt.slice(5) };
            for (const k of chartMetrics) { const v = Number(cell[k] || 0); point[k] = v; means[k] += v; }
            return point;
        });
        const n = raw.length;
        for (const k of chartMetrics) means[k] = means[k] / n || 1;
        let gMin = 100, gMax = 100;
        const norm = raw.map(p => {
            const out = { dt: p.dt };
            for (const k of chartMetrics) { const pct = (p[k] / means[k]) * 100; out[k] = pct; if (pct < gMin) gMin = pct; if (pct > gMax) gMax = pct; }
            return out;
        });
        const pad = Math.max((gMax - gMin) * 0.15, 5);
        return { chartData: norm, chartRaw: raw, chartDomain: [Math.floor(gMin - pad), Math.ceil(gMax + pad)] };
    }, [dates, chartMetrics, activeData]);

    const fmtValue = (m, v) => {
        if (v == null) return '-';
        if (m.fmt === 'pct1') return `${fmtNum(v, 1)}%`;
        if (m.fmt === 'pct2') return `${fmtNum(v, 2)}%`;
        if (m.fmt === 'dec1') return fmtNum(v, 1);
        return fmtNum(v, 0);
    };

    const calcDeltaPct = (curr, prev) => {
        if (prev == null || prev === 0 || curr == null) return null;
        return ((curr - prev) / Math.abs(prev)) * 100;
    };

    const renderCellDelta = (m, currVal, prevVal) => {
        const formatted = fmtValue(m, currVal);
        if (!showDelta || prevVal == null || currVal == null) return formatted;
        const d = calcDeltaPct(Number(currVal), Number(prevVal));
        if (d == null || !Number.isFinite(d) || Math.abs(d) < 0.01) return formatted;
        const isGood = m.lowerBetter ? d < 0 : d > 0;
        const color = isGood ? 'success.main' : 'error.main';
        const Arr = d > 0 ? ArrowUpIcon : ArrowDownIcon;
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <span>{formatted}</span>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Arr sx={{ color, fontSize: 16, m: -0.25 }} />
                    <Typography variant="caption" sx={{ color, fontSize: '0.65rem', lineHeight: 1 }}>{fmtNum(Math.abs(d), 1)}%</Typography>
                </Box>
            </Box>
        );
    };

    const ChartTooltipContent = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        const idx = chartData.findIndex(d => d.dt === label);
        const rawPoint = idx >= 0 ? chartRaw[idx] : null;
        return (
            <Paper sx={{ p: 1, fontSize: '0.8rem' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>{label}</Typography>
                {payload.map(entry => {
                    const m = METRIC_ROWS.find(mr => mr.key === entry.dataKey);
                    return (
                        <Box key={entry.dataKey} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: entry.color, flexShrink: 0 }} />
                            <span>{m?.label || entry.dataKey}: <b>{m ? fmtValue(m, rawPoint?.[entry.dataKey]) : entry.value}</b></span>
                        </Box>
                    );
                })}
            </Paper>
        );
    };

    // --- Summary KPI helpers ---
    const kpiDelta = (curr, prev) => {
        if (!prev || prev === 0) return null;
        return ((curr - prev) / Math.abs(prev) * 100).toFixed(1);
    };

    const KpiRow = ({ label, curr, prev, suffix = '' }) => {
        const d = kpiDelta(curr, prev);
        const isUp = d > 0;
        return (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', color: 'text.secondary' }}>{label}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{fmtNum(curr, 0)}{suffix}</Typography>
                    {d != null && Math.abs(d) >= 0.1 && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {isUp ? <ArrowUpIcon sx={{ fontSize: 16, color: 'success.main' }} /> : <ArrowDownIcon sx={{ fontSize: 16, color: 'error.main' }} />}
                            <Typography variant="caption" sx={{ color: isUp ? 'success.main' : 'error.main', fontWeight: 600 }}>
                                {Math.abs(d)}%
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        );
    };

    // --- Compact styles ---
    const xs = { py: 0.15, px: 0.5, fontSize: '0.7rem', whiteSpace: 'nowrap' };
    const xsH = { ...xs, fontWeight: 'bold', bgcolor: 'grey.100' };

    const MoversTable = ({ title, data, metricLabel, isPosition = false, isPercent = false, color }) => (
        <Paper sx={{ p: 1, height: '100%' }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color, display: 'block', mb: 0.5 }}>{title}</Typography>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={xsH}>Артикул</TableCell>
                        <TableCell sx={xsH} align="right">Было</TableCell>
                        <TableCell sx={xsH} align="right">Стало</TableCell>
                        <TableCell sx={xsH} align="right">{metricLabel}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.map((r, i) => (
                        <TableRow key={i}>
                            <TableCell sx={{ ...xs, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                {r.offer_id || String(r.nm_id)}
                            </TableCell>
                            <TableCell sx={xs} align="right">
                                {isPercent ? `${fmtNum(r.prev_value, 1)}%` : fmtNum(r.prev_value, isPosition ? 1 : 0)}
                            </TableCell>
                            <TableCell sx={xs} align="right">
                                {isPercent ? `${fmtNum(r.curr_value, 1)}%` : fmtNum(r.curr_value, isPosition ? 1 : 0)}
                            </TableCell>
                            <TableCell sx={{ ...xs, color, fontWeight: 600 }} align="right">
                                {r.delta > 0 ? '+' : ''}{isPercent || !isPosition ? `${fmtNum(r.delta, 1)}${isPercent ? ' п.п.' : '%'}` : fmtNum(r.delta, 1)}
                            </TableCell>
                        </TableRow>
                    ))}
                    {!data.length && (
                        <TableRow><TableCell colSpan={4} align="center" sx={xs}>Нет данных</TableCell></TableRow>
                    )}
                </TableBody>
            </Table>
        </Paper>
    );

    const kwGridCols = useMemo(() => {
        const fmtPos = (v) => (v == null || v === '' ? '-' : Number(v).toFixed(1));
        const numOrNull = (v) => (v == null || v === '' ? null : Number(v));
        return [
            { field: 'text', headerName: 'Ключевое слово', flex: 1, minWidth: 200, filterable: true },
            { field: 'frequency', headerName: 'Частота', width: 100, type: 'number', valueGetter: (v) => Number(v) || 0 },
            { field: 'products', headerName: 'Товаров', width: 90, type: 'number', valueGetter: (v) => Number(v) || 0 },
            { field: 'open_card', headerName: 'Переходы', width: 100, type: 'number', valueGetter: (v) => Number(v) || 0 },
            { field: 'add_to_cart', headerName: 'Корзина', width: 90, type: 'number', valueGetter: (v) => Number(v) || 0 },
            { field: 'orders', headerName: 'Заказы', width: 90, type: 'number', valueGetter: (v) => Number(v) || 0 },
            { field: 'avg_position', headerName: 'Позиция WB', width: 100, type: 'number', valueGetter: (v) => Number(v) || 0 },
            { field: 'position_mpstats', headerName: 'Поз. MPSTATS', width: 120, type: 'number', valueGetter: numOrNull, valueFormatter: fmtPos },
            { field: 'ad_position_mpstats', headerName: 'Поз. реклама', width: 120, type: 'number', valueGetter: numOrNull, valueFormatter: fmtPos },
            { field: 'organic_position_mpstats', headerName: 'Поз. органика', width: 120, type: 'number', valueGetter: numOrNull, valueFormatter: fmtPos },
            { field: 'cr_cart', headerName: 'CR корз. %', width: 100, type: 'number', valueGetter: (v) => Number(v) || 0, valueFormatter: (v) => `${Number(v).toFixed(1)}%` },
            { field: 'cr_order', headerName: 'CR зак. %', width: 100, type: 'number', valueGetter: (v) => Number(v) || 0, valueFormatter: (v) => `${Number(v).toFixed(2)}%` },
        ];
    }, []);

    const { current: cur, prev: prv } = summary;

    return (
        <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>Поисковая аналитика WB</Typography>

            {/* --- Filters --- */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={6} sm={4} md={1.5}>
                        <TextField select label="Разбивка" value={breakdown} onChange={e => setBreakdown(e.target.value)} size="small" fullWidth>
                            {BREAKDOWNS.map(b => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={8} md={3}>
                        <DateRangeField valueFrom={dateFrom} valueTo={dateTo} onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }} />
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <ProductFilters value={filters} onChange={setFilters} showMp={false} />
                </Box>
                <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
                    <Grid item xs={6} sm={3} md={1.5}>
                        <Button variant="contained" onClick={load} disabled={loading} fullWidth>
                            {loading ? <CircularProgress size={20} /> : 'Загрузить'}
                        </Button>
                    </Grid>
                    <Grid item xs={6} sm={3} md={2}>
                        <FormControlLabel
                            control={<Checkbox size="small" checked={showDelta} onChange={() => setShowDelta(p => !p)} />}
                            label={<Typography variant="body2">Показывать изменения</Typography>}
                        />
                    </Grid>
                </Grid>
            </Paper>

            {/* --- KPI Cards --- */}
            {(cur.total_products > 0 || pivotRows.length > 0) && (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6} md={2}>
                        <Paper sx={{ p: 2, height: '100%' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Позиции в поиске</Typography>
                            <KpiRow label="ТОП 10" curr={cur.top10} prev={prv.top10} />
                            <KpiRow label="ТОП 50" curr={cur.top50} prev={prv.top50} />
                            <KpiRow label="ТОП 100" curr={cur.top100} prev={prv.top100} />
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Paper sx={{ p: 2, height: '100%' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Видимость</Typography>
                            <KpiRow label="Высокая (>70%)" curr={cur.vis_high} prev={prv.vis_high} />
                            <KpiRow label="Средняя (30-70%)" curr={cur.vis_mid} prev={prv.vis_mid} />
                            <KpiRow label="Низкая (<30%)" curr={cur.vis_low} prev={prv.vis_low} />
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Paper sx={{ p: 2, height: '100%' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>В рекламе</Typography>
                            <KpiRow label="Рекламируемых" curr={cur.advertised} prev={prv.advertised} />
                            <KpiRow label="Всего товаров" curr={cur.total_products} prev={prv.total_products} />
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Paper sx={{ p: 2, height: '100%' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Переходы</Typography>
                            <KpiRow label="Всего" curr={cur.open_card} prev={prv.open_card} />
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Paper sx={{ p: 2, height: '100%' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Заказы из поиска</Typography>
                            <KpiRow label="Всего" curr={cur.orders} prev={prv.orders} />
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Paper sx={{ p: 2, height: '100%' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>CR воронка</Typography>
                            <KpiRow label="В корзину" curr={cur.open_card > 0 ? +(cur.add_to_cart / cur.open_card * 100).toFixed(1) : 0} prev={prv.open_card > 0 ? +(prv.add_to_cart / prv.open_card * 100).toFixed(1) : 0} suffix="%" />
                            <KpiRow label="В заказ" curr={cur.add_to_cart > 0 ? +(cur.orders / cur.add_to_cart * 100).toFixed(1) : 0} prev={prv.add_to_cart > 0 ? +(prv.orders / prv.add_to_cart * 100).toFixed(1) : 0} suffix="%" />
                        </Paper>
                    </Grid>
                </Grid>
            )}

            {/* --- Entity selector + Chart + Transposed Table --- */}
            {pivotRows.length > 0 && (<>
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                        <TextField select label={BREAKDOWNS.find(b => b.value === breakdown)?.label || 'Объект'} value={selectedEntity} onChange={e => { setSelectedEntity(e.target.value); setChartMetrics([]); }} size="small" sx={{ minWidth: 220 }}>
                            <MenuItem value="__TOTAL__">Итого (все)</MenuItem>
                            {entities.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                        </TextField>
                        <FormControlLabel control={<Checkbox size="small" checked={showDelta} onChange={() => setShowDelta(d => !d)} />} label={<Typography variant="body2">Изменения</Typography>} />
                    </Box>

                    {chartMetrics.length > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            {chartMetrics.map((k, i) => {
                                const m = METRIC_ROWS.find(mr => mr.key === k);
                                return <Chip key={k} label={m?.label || k} size="small" sx={{ borderLeft: `4px solid ${CHART_COLORS[i % CHART_COLORS.length]}` }} onDelete={() => toggleChartMetric(k)} />;
                            })}
                            {chartMetrics.length > 1 && <Chip label="Сбросить" size="small" variant="outlined" onClick={() => setChartMetrics([])} onDelete={() => setChartMetrics([])} />}
                        </Box>
                    )}
                    {chartMetrics.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Нажмите на строку в таблице, чтобы добавить показатель на график</Typography>
                    )}
                    <Box sx={{ height: 280 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={chartData}>
                                <defs>
                                    {chartMetrics.map((k, i) => (
                                        <linearGradient key={k} id={`gradS_${k}`} x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0.25} />
                                            <stop offset="95%" stopColor={CHART_COLORS[i % CHART_COLORS.length]} stopOpacity={0} />
                                        </linearGradient>
                                    ))}
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="dt" tick={{ fontSize: 12 }} />
                                <YAxis tick={{ fontSize: 11 }} width={40} domain={chartDomain} tickFormatter={v => `${v}%`} />
                                {chartMetrics.length > 0 && <ReferenceLine y={100} stroke="#999" strokeDasharray="4 4" label={{ value: 'среднее', position: 'right', fontSize: 10, fill: '#999' }} />}
                                <Tooltip content={<ChartTooltipContent />} />
                                <Legend />
                                {chartMetrics.map((k, i) => (
                                    <Area key={k} type="monotone" dataKey={k} name={METRIC_ROWS.find(mr => mr.key === k)?.label || k} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} fill={`url(#gradS_${k})`} />
                                ))}
                            </AreaChart>
                        </ResponsiveContainer>
                    </Box>
                </Paper>

                <TableContainer component={Paper} sx={{ overflowX: 'auto', mb: 2 }}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...headerStyle, position: 'sticky', left: 0, zIndex: 2, bgcolor: 'grey.100' }}>Показатель</TableCell>
                                {dates.map(dt => <TableCell key={dt} sx={headerStyle} align="right">{dt}</TableCell>)}
                                {dates.length > 0 && <TableCell sx={{ ...headerStyle, fontWeight: 'bold' }} align="right">Итого</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(() => {
                                let lastGroup = null;
                                return METRIC_ROWS.map((m) => {
                                    const isFirst = m.group !== lastGroup;
                                    lastGroup = m.group;
                                    const gColor = METRIC_GROUP_COLORS[m.group] || 'white';
                                    const cIdx = chartMetrics.indexOf(m.key);
                                    const onChart = cIdx !== -1;
                                    const cColor = onChart ? CHART_COLORS[cIdx % CHART_COLORS.length] : null;

                                    return (
                                        <TableRow key={m.key} hover sx={{ borderTop: isFirst ? '2px solid #bdbdbd' : 'none', cursor: 'pointer', ...(onChart && { bgcolor: '#f0f0ff' }) }} onClick={() => toggleChartMetric(m.key)}>
                                            <TableCell sx={{ ...cellStyle, position: 'sticky', left: 0, bgcolor: onChart ? '#e8e8ff' : (isFirst ? gColor : '#fff'), fontWeight: isFirst ? 'bold' : 500, borderLeft: onChart ? `4px solid ${cColor}` : `4px solid ${gColor}`, zIndex: 1 }}>
                                                {m.label}
                                            </TableCell>
                                            {dates.map((dt, dtIdx) => {
                                                const cell = activeData.dayMap[dt];
                                                const prevDt = dtIdx > 0 ? dates[dtIdx - 1] : null;
                                                const prevCell = prevDt ? activeData.dayMap[prevDt] : null;
                                                return (
                                                    <TableCell key={dt} sx={{ ...cellStyle, bgcolor: isFirst ? gColor : 'inherit' }} align="right">
                                                        {renderCellDelta(m, cell?.[m.key], prevCell?.[m.key])}
                                                    </TableCell>
                                                );
                                            })}
                                            {dates.length > 0 && (
                                                <TableCell sx={{ ...cellStyle, fontWeight: 'bold', bgcolor: isFirst ? gColor : 'grey.100' }} align="right">
                                                    {fmtValue(m, activeData.totals[m.key])}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    );
                                });
                            })()}
                        </TableBody>
                    </Table>
                </TableContainer>
            </>)}

            {/* --- Keywords DataGrid --- */}
            {keywords.length > 0 && (
                <Paper sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ p: 1.5, pb: 0 }}>Ключевые слова</Typography>
                    <DataGrid
                        rows={keywords}
                        columns={kwGridCols}
                        density="compact"
                        autoHeight
                        pageSizeOptions={[25, 50, 100]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 25 } },
                            sorting: { sortModel: [{ field: 'orders', sort: 'desc' }] },
                            filter: { filterModel: { items: [] } },
                        }}
                        disableRowSelectionOnClick
                        filterMode="client"
                        sortingMode="client"
                        slots={{ toolbar: () => (
                            <Box sx={{ px: 1.5, pt: 1 }}>
                                <GridToolbarQuickFilter debounceMs={300} sx={{ width: 300 }} />
                            </Box>
                        )}}
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-cell': { py: 0.25, fontSize: '0.8rem' },
                            '& .MuiDataGrid-columnHeaders': { fontSize: '0.8rem', bgcolor: 'grey.50' },
                        }}
                    />
                </Paper>
            )}

            {/* --- Movers: up + down --- */}
            {(moversUp.position.length > 0 || moversDown.position.length > 0) && (
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                        <MoversTable title="Рост позиций" data={moversUp.position} metricLabel="Δ" isPosition color="success.main" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <MoversTable title="Рост трафика" data={moversUp.traffic} metricLabel="Δ%" color="success.main" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <MoversTable title="Рост конверсии" data={moversUp.conversion} metricLabel="Δ п.п." isPercent color="success.main" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <MoversTable title="Падение позиций" data={moversDown.position} metricLabel="Δ" isPosition color="error.main" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <MoversTable title="Падение трафика" data={moversDown.traffic} metricLabel="Δ%" color="error.main" />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <MoversTable title="Падение конверсии" data={moversDown.conversion} metricLabel="Δ п.п." isPercent color="error.main" />
                    </Grid>
                </Grid>
            )}

            {!loading && !pivotRows.length && (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">Нажмите «Загрузить» для получения данных</Typography>
                </Paper>
            )}
        </Box>
    );
}
